import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dropdown,
  Flex,
  H5,
  Icon,
  Modal,
  M,
  Tooltip,
} from 'design-system-web';
import { Input } from 'components';
import { Alert } from 'components/lds';

import styles from './TargetTimesTab.scss';

export type DropdownOption = { label: string; value: string };

type Props = {
  isOpen: boolean;
  onClose: () => void;
  formError: string | null;
  categoryItems: DropdownOption[];
  categoryValue: string | null;
  onCategoryChange: (value: string) => void;
  categoryDisabled?: boolean;
  issueItems: DropdownOption[];
  issueDropdownValue: string | null;
  onIssueChange: (value: string) => void;
  issueDisabled: boolean;
  respHours: string;
  onRespHoursChange: (v: string) => void;
  respMinutes: string;
  onRespMinutesChange: (v: string) => void;
  compHours: string;
  onCompHoursChange: (v: string) => void;
  compMinutes: string;
  onCompMinutesChange: (v: string) => void;
  submitLoading: boolean;
  onSubmit: () => void;
};

export function TargetTimesCreateModal({
  isOpen,
  onClose,
  formError,
  categoryItems,
  categoryValue,
  onCategoryChange,
  categoryDisabled = false,
  issueItems,
  issueDropdownValue,
  onIssueChange,
  issueDisabled,
  respHours,
  onRespHoursChange,
  respMinutes,
  onRespMinutesChange,
  compHours,
  onCompHoursChange,
  compMinutes,
  onCompMinutesChange,
  submitLoading,
  onSubmit,
}: Props) {
  const { t } = useTranslation();

  const modalLabelWithTooltip = (labelKey: string, tooltipKey: string) => (
    <div className={styles.modalFieldLabel}>
      <M>{t(labelKey)}</M>
      <Tooltip TooltipComponent={t(tooltipKey)}>
        <button
          type="button"
          className={styles.infoTrigger}
          aria-label={t(tooltipKey)}
        >
          <Icon name="info-circle" set="FontAwesome" type="far" />
        </button>
      </Tooltip>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      testId="target-times-create-modal"
      closeButtonSize="small"
      size="large"
    >
      <Flex direction="column" gap={2} className={styles.modalBody}>
        <H5>{t`web.admin.serviceRequest.settings.targetTimes.modalTitle`}</H5>
        <M className={styles.modalIntro}>
          {t`web.admin.serviceRequest.settings.targetTimes.modalDisclaimer`}
        </M>
        {formError ? <Alert type="error">{formError}</Alert> : null}

        <div className={styles.modalSection}>
          <Dropdown
            id="target-times-category-select"
            testId="target-times-category-dropdown"
            doTranslation={false}
            className={styles.dropdownField}
            isFullWidth
            items={categoryItems}
            value={categoryValue}
            onChange={item => onCategoryChange(String(item.value))}
            label={t('web.admin.serviceRequest.settings.targetTimes.form.category')}
            placeholder={t(
              'web.admin.serviceRequest.settings.targetTimes.form.categoryPlaceholder'
            )}
            fixedLabel
            isRequired
            menuPosition="fixed"
            disabled={categoryDisabled}
          />
        </div>

        <div className={styles.modalSection}>
          <Dropdown
            id="target-times-issue-select"
            testId="target-times-issue-dropdown"
            doTranslation={false}
            className={styles.dropdownField}
            isFullWidth
            items={issueItems}
            value={issueDropdownValue}
            onChange={item => onIssueChange(String(item.value))}
            label={t('web.admin.serviceRequest.settings.targetTimes.form.issue')}
            placeholder={t(
              'web.admin.serviceRequest.settings.targetTimes.form.issuePlaceholder'
            )}
            fixedLabel
            disabled={issueDisabled}
            menuPosition="fixed"
          />
        </div>

        <div className={styles.modalSection}>
          {modalLabelWithTooltip(
            'web.admin.serviceRequest.settings.targetTimes.form.targetResponseTime',
            'web.admin.serviceRequest.settings.targetTimes.tooltip.response'
          )}
          <div className={styles.timePairRow}>
            <div className={styles.timeWithUnit}>
              <Input
                fieldName="targetTimesModalRespH"
                label={t('web.admin.serviceRequest.settings.targetTimes.form.hours')}
                value={respHours}
                onChange={onRespHoursChange}
                type="number"
                min={0}
                showClear={false}
                testId="target-times-response-hours"
                fixedLabel
              />
              <span className={styles.durationUnit}>
                {t`web.admin.serviceRequest.settings.targetTimes.form.hoursSuffix`}
              </span>
            </div>
            <div className={styles.timeWithUnit}>
              <Input
                fieldName="targetTimesModalRespM"
                label={t('web.admin.serviceRequest.settings.targetTimes.form.minutes')}
                value={respMinutes}
                onChange={onRespMinutesChange}
                type="number"
                min={0}
                input={{ max: 59, min: 0 }}
                showClear={false}
                testId="target-times-response-minutes"
                fixedLabel
              />
              <span className={styles.durationUnit}>
                {t`web.admin.serviceRequest.settings.targetTimes.form.minutesSuffix`}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.modalSection}>
          {modalLabelWithTooltip(
            'web.admin.serviceRequest.settings.targetTimes.form.targetCompletionTime',
            'web.admin.serviceRequest.settings.targetTimes.tooltip.completion'
          )}
          <div className={styles.timePairRow}>
            <div className={styles.timeWithUnit}>
              <Input
                fieldName="targetTimesModalCompH"
                label={t('web.admin.serviceRequest.settings.targetTimes.form.hours')}
                value={compHours}
                onChange={onCompHoursChange}
                type="number"
                min={0}
                showClear={false}
                testId="target-times-completion-hours"
                fixedLabel
              />
              <span className={styles.durationUnit}>
                {t`web.admin.serviceRequest.settings.targetTimes.form.hoursSuffix`}
              </span>
            </div>
            <div className={styles.timeWithUnit}>
              <Input
                fieldName="targetTimesModalCompM"
                label={t('web.admin.serviceRequest.settings.targetTimes.form.minutes')}
                value={compMinutes}
                onChange={onCompMinutesChange}
                type="number"
                min={0}
                input={{ max: 59, min: 0 }}
                showClear={false}
                testId="target-times-completion-minutes"
                fixedLabel
              />
              <span className={styles.durationUnit}>
                {t`web.admin.serviceRequest.settings.targetTimes.form.minutesSuffix`}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <Button
            variant="primary"
            size="large"
            loading={submitLoading}
            onClick={() => {
              onSubmit();
            }}
            testId="target-times-submit"
          >
            {t`web.admin.serviceRequest.settings.targetTimes.form.submit`}
          </Button>
          <Button variant="secondary" size="large" onClick={onClose}>
            {t`web.admin.serviceRequest.settings.targetTimes.form.cancel`}
          </Button>
        </div>
      </Flex>
    </Modal>
  );
}
